import { useEffect, useRef, useState } from 'react'

import { useSearchParams } from 'react-router'

import {
  type PlanDetailDeeplink,
  parsePlanDetailDeeplink,
} from '~/pages/ProcurementPlanDetail/models/deeplink'

/**
 * AC-PPD-022 の一時ハイライトの表示時間 (ms)。
 *
 * design `plan/submission-detail.html` は行ハイライトを解除しない (時間の指定が無い)。
 * spec は「一時的」とだけ定めるので、同じ「別画面からの遷移直後に対象を一時強調する」
 * 既存実装 (`Supply48Data/hooks/useSupply48ScrollHighlight.ts`、AC-S4D-052) の
 * 3 秒に揃える (画面ごとに長さが違うと利用者が戸惑うため)。
 */
export const DEEPLINK_HIGHLIGHT_DURATION_MS = 3000

/**
 * 提出管理からの deeplink (AC-PPD-022 / 022-1、AC-PPP-004) を読み、計画詳細の表示が
 * 揃った最初の 1 回だけハイライト対象を返し、`DEEPLINK_HIGHLIGHT_DURATION_MS` 後に解除する。
 *
 * `isReady` (= 計画詳細を表示できる状態) になるまでは判定を待つ。判定はページの生存中に
 * 1 度きりで、フィルタ操作で別計画へ移ってもハイライトを再発火しない (遷移先 URL は
 * クエリを持たない)。判定後に計画 (`planId`) やクエリが変わったら「遷移直後」は終わった
 * ものとして解除する (解除前に別計画へ移ると、同じ取引先コードを持つ無関係な計画の
 * セルを強調してしまうため。`useSupply48ScrollHighlight` と同じ扱い)。
 * 列の照合とスクロールは描画側 (PlanTable) が行い、本 hook は DOM に触れない。
 */
export function useDeeplinkHighlight({
  isReady,
  planId,
}: {
  isReady: boolean
  planId: string
}): PlanDetailDeeplink | undefined {
  const [searchParams] = useSearchParams()
  const triggeredRef = useRef(false)
  const [highlight, setHighlight] = useState<PlanDetailDeeplink | undefined>(
    undefined,
  )

  const triggeredPlanIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (triggeredRef.current) {
      if (planId !== triggeredPlanIdRef.current) setHighlight(undefined)
      return
    }
    if (!isReady) return
    triggeredRef.current = true
    triggeredPlanIdRef.current = planId
    const parsed = parsePlanDetailDeeplink(searchParams)
    if (parsed !== undefined) setHighlight(parsed)
  }, [isReady, searchParams, planId])

  useEffect(() => {
    if (highlight === undefined) return undefined
    const timer = setTimeout(() => {
      setHighlight(undefined)
    }, DEEPLINK_HIGHLIGHT_DURATION_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [highlight])

  return highlight
}
